import React, { useState } from 'react';
import moment from 'moment';
import { Tooltip, IconButton, Dialog, DialogContent, DialogTitle } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateSvg from "../../../../styles/svg/date-range.svg";
import CommonlyUsedComponents from "../../../board/components/datePickerCalender/datePicker";

/**
 * DueDate component - displays days left or date picker icon
 * @param {Object} props - Component props
 * @param {string} props.dueDate - ISO date string for the due date
 * @param {function} props.onDateChange - Function to call when date is updated
 * @param {number} props.taskId - ID of the task
 */
function DueDate({ dueDate, onDateChange, taskId }) {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  // Calculate days left if due date exists
  const getDaysLeft = () => {
    if (!dueDate) return null;
    const now = moment();
    const due = moment(dueDate);
    const diffInSeconds = due.diff(now, 'seconds');
    const diffInMinutes = due.diff(now, 'minutes');
    const diffInHours = due.diff(now, 'hours');
    const diffInDays = due.diff(now, 'days');
    const diffInWeeks = due.diff(now, 'weeks');
    const diffInMonths = due.diff(now, 'months');
    const diffInYears = due.diff(now, 'years');

    // Handle past times by adding a minus sign
    const formatTime = (value) => value < 0 ? `-${Math.abs(value)}` : value;

    if (diffInSeconds < 0) {
      if (diffInYears < -1) {
        return `${formatTime(diffInYears)}y`;
      } else if (diffInMonths <= -1) {
        return `${formatTime(diffInMonths)}mo`;
      } else if (diffInWeeks < -1) {
        return `${formatTime(diffInWeeks)}w`;
      } else if (diffInDays < -1) {
        return `${formatTime(diffInDays)}d`;
      } else if (diffInHours < -24) {
        return `${formatTime(diffInDays)}d`;
      } else if (diffInHours < -1) {
        return `${formatTime(diffInHours)}h`;
      } else if (diffInMinutes < -1) {
        return `${formatTime(diffInMinutes)}m`;
      } else {
        return `${formatTime(diffInSeconds)}s`;
      }
    } else if (diffInSeconds < 60) {
      return `${formatTime(diffInSeconds)}s`;
    } else if (diffInMinutes < 60) {
      return `${formatTime(diffInMinutes)}m`;
    } else if (diffInHours < 24) {
      return `${formatTime(diffInHours)}h`;
    } else if (diffInDays < 7) {
      return `${formatTime(diffInDays)}d`;
    } else if (diffInWeeks <= 4) {
      return `${formatTime(diffInWeeks)}w`;
    } else if (diffInMonths < 12) {
      return `${formatTime(diffInMonths)}mo`;
    } else {
      return `${formatTime(diffInYears)}y`;
    }

  };
  
  // Handle opening date picker
  const handleOpenDatePicker = (e) => {
    e.stopPropagation(); // Prevent task click event
    setIsDatePickerOpen(true);
  };
  
  // Handle closing date picker
  const handleCloseDatePicker = () => {
    setIsDatePickerOpen(false);
  };
  
  // Handle date selection
  const handleDateChange = (date) => {
    if (onDateChange) {
      onDateChange(taskId, date);
    }
    setIsDatePickerOpen(false);
  };
  
  // Get days left text
  const daysLeft = getDaysLeft();
  
  // Get appropriate class based on days left
  const getDueDateClass = () => {
    if (!dueDate) return '';
    
    const today = moment().startOf('day');
    const due = moment(dueDate).startOf('day');
    const diff = due.diff(today, 'days');
    
    if (diff < 0) return 'overdue';
    if (diff <= 2) return 'soon';
    return '';
  };
  
  return (
    <>
      {dueDate ? (
        // If due date exists, show days left with calendar icon
        <div className={`task-due-date ${getDueDateClass()}`}>
          {/* <CalendarTodayIcon className="calendar-icon" /> */}
          <span onClick={handleOpenDatePicker}>{daysLeft}</span>
        </div>
      ) : (
        // If no due date, show date picker icon
        <Tooltip title="Add due date">
          <IconButton 
            className="task-action date"
            size="small"
            onClick={handleOpenDatePicker}
          >
            <img src={DateSvg} alt="add date" className="date-icon" />
          </IconButton>
        </Tooltip>
      )}
      
      {/* Date Picker Dialog */}
      <Dialog 
        open={isDatePickerOpen} 
        onClose={handleCloseDatePicker}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {dueDate ? 'Update Due Date' : 'Add Due Date'}
        </DialogTitle>
        <DialogContent>
            
          <CommonlyUsedComponents 
            date={dueDate ? moment(dueDate) : null}
            setDate={handleDateChange}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DueDate;